import React, {useState} from 'react';
import {Box, Typography, CircularProgress} from '@mui/material';
import {NAVY, ORANGE, PageHeader, BandChip, UseWithCareBanner, bandColor, riskBandColor, MarkerBar, MARKER_SECONDARY, RISK_BAND_COLORS} from '../components/widgets.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedItem} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortBars} from '../../DashboardCommon/barSort';
import {riskGroupLabel} from '../../DashboardCommon/riskGroups';

// A figure as a whole number (the rounding every dashboard uses); null when it is not sent (or under the n floor).
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : null);

/* Static metadata for sensitive risks */
const RISK_METADATA = {
    "Sexual Content Exposure": { sensitive: true },
    "Offline Meetings with Online Strangers": { sensitive: true },
    "Offline Meeting with Stranger": { sensitive: true },
    "Sexual Conversation": { sensitive: true },
    "Unwanted Online Sexual Contact": { sensitive: true },
    "Unwanted Sexual Contact": { sensitive: true },
    "AI-Generated Sexual Image Risk": { sensitive: true },
};
const LEGEND = Object.keys(RISK_BAND_COLORS);

const RiskRow = ({row}) => (
    <Box sx={{
        display: 'grid',
        // Last track must fit "Priority Concern" (the longest band label) at
        // BandChip's font/padding without the pill's fixed-width background
        // shrinking below the (non-wrapping) text and letting it spill out.
        gridTemplateColumns: {xs: '200px 1fr 40px 140px', md: '300px 1fr 40px 140px'},
        alignItems: 'center',
        gap: 2,
        mb: 1.8,
    }}>
        <Typography sx={{fontSize: '13px', color: NAVY, lineHeight: 1.3}}>
            <strong>{row.code}</strong>&nbsp;&nbsp;{row.label}
            {row.sensitive && <Typography component="span" sx={{fontSize: '11px', color: '#9CA3AF', ml: 1}}>· sensitive</Typography>}
            {/* In the value-sorted (flattened) list each row names its risk group. */}
            {row.groupLabel && <Typography component="span" sx={{display: 'block', fontSize: '11px', color: '#6A7282', mt: 0.25}}>{row.groupLabel}</Typography>}
        </Typography>
        {row.suppressed ? <Box sx={{gridColumn: '2 / span 3'}}><SuppressedValue/></Box> : (
            <>
                <MarkerBar score={row.pct} color={riskBandColor(row.band)} global={row.global} height={12} unit="percent"/>
                <Typography sx={{fontSize: '14px', fontWeight: 800, color: NAVY, textAlign: 'right'}}>{row.pct === null ? '—' : `${row.pct}%`}</Typography>
                <Box>{row.band && <BandChip label={row.band} color={riskBandColor(row.band)}/>}</Box>
            </>
        )}
    </Box>
);

const CYBER_RISK_PROFILE_SECTION_CODE = 'S_15a';
const CARD_SECTION = CYBER_RISK_PROFILE_SECTION_CODE;

const RiskProfileTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)

    // Find the section with code "S_15a"
    const s15aSection = dashboardData?.cards?.sections?.find(
        (section) => section.section_code === CYBER_RISK_PROFILE_SECTION_CODE
    );

    if (loading) {
        return <CircularLoader />;
    }

    if (!s15aSection) {
        return (
            <NoDataAvailable />
        );
    }

    const s15aCards = s15aSection?.cards || [];

    // Get title card and left line card
    const titleCard = s15aCards.find(c => c.card_type === 'TitleCard');
    const headerTitle = "Portfolio Cyber-Risk Profile";   // static copy (frontend-owned): "Cyber-Risk" as an adjective
    const headerSubtitle = titleCard?.footer || "Which cyber risks are most prevalent, and which need action first?";

    const leftLineCard = s15aCards.find(c => c.card_type === 'LeftLineCard');

    let useWithCareText = "Risk prevalence is a status, not a score — higher means more students affected. Differences should prompt support, not assumptions. Subgroups under 10 students are suppressed.";
    if (leftLineCard?.leftline_html) {
        const cleaned = leftLineCard.leftline_html
            .replace(/<p>/gi, '')
            .replace(/<\/p>/gi, '')
            .replace(/<strong>Use with care\.?<\/strong>/gi, '')
            .trim();
        if (cleaned) {
            useWithCareText = cleaned;
        }
    }

    const barChartCard = s15aCards.find(c => c.card_type === 'BarChartBMCard');
    const items = barChartCard?.items;

    let groups = [];

    if (items && items.length > 0) {
        const groupMap = new Map();

        items.forEach((item, index) => {
            const rawLabel = item.label || '';

            // Extract category (parent heading) from parent_label
            let category = item.parent_label || item.parent_code;
            if (!category) {
                if (rawLabel.includes(' · ')) {
                    category = rawLabel.split(' · ')[0]?.trim();
                } else if (rawLabel.toUpperCase().includes('ANY CYBER RISK')) {
                    category = 'Headline';
                } else {
                    category = 'Other Risk';
                }
            }

            // Extract risk label
            let riskName = rawLabel;
            if (!item.parent_label && rawLabel.includes(' · ')) {
                riskName = rawLabel.split(' · ').slice(1).join(' · ')?.trim();
            }
            // The same heading on every dashboard (LA-ND, PD, SND, SD-03Nov-01).
            category = riskGroupLabel(riskName, category);

            // Extract code (numbering) from sublabel
            let code = '';
            if (item.sublabel) {
                const match = item.sublabel.match(/\d+/);
                if (match) {
                    code = String(match[0]).padStart(2, '0');
                }
            }
            if (!code) {
                const meta = RISK_METADATA[riskName] || RISK_METADATA[rawLabel] || {};
                code = meta.code || String(index + 1).padStart(2, '0');
            }

            const meta = RISK_METADATA[riskName] || RISK_METADATA[rawLabel] || {};
            const sensitive = item.sensitive !== undefined ? item.sensitive : (meta.sensitive || false);

            const nationalVal = item.benchmark_values?.find(b => b.name?.toLowerCase()?.includes('national'))?.value;
            const globalVal = item.benchmark_values?.find(b => b.name?.toLowerCase()?.includes('global'))?.value;

            const row = {
                code,
                label: riskName,
                sensitive,
                suppressed: isSuppressedItem(item),
                pct: isSuppressedItem(item) ? null : num(item.value),      // null: not sent, or under the n floor
                band: isSuppressedItem(item) ? null : (item.band || null),
                national: num(nationalVal),
                global: num(globalVal),
            };

            if (!groupMap.has(category)) {
                groupMap.set(category, {
                    header: category,
                    rows: []
                });
            }
            groupMap.get(category).rows.push(row);
        });

        groups = Array.from(groupMap.values());
    }
    // Group order keeps the risk families and their headings; the value modes rank every risk in one list (suppressed last).
    const shownGroups = sortBars(groups, sortMode, {itemsKey: 'rows'});

    return (
        <Box>
            {/*<PageHeader*/}
            {/*    title={headerTitle}*/}
            {/*    subtitle={headerSubtitle}*/}
            {/*/>*/}


            <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1}}>
                    <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>{/* Fixed per PD slide 48 — the card's title says "School", but this is the partner's whole portfolio. */}
                        Portfolio Cyber-Risk Profile
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                            {LEGEND.map((l) => <BandChip key={l} label={l} color={riskBandColor(l)}/>)}
                        </Box>
                        <SortToggle value={sortMode} onChange={setSortMode}/>
                    </Box>
                </Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 3, mb: 2.5, flexWrap: 'wrap'}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
                        <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Global avg</Typography>
                    </Box>
                </Box>

                {shownGroups.map((g, gi) => (
                    <Box key={`${g.header || 'all'}-${gi}`} sx={{mb: 2}}>
                        {g.header && (
                            <Typography sx={{fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#FF6900', mt: 1.5, mb: 1}}>
                                {g.header}
                            </Typography>
                        )}
                        {g.rows.map((r) => <RiskRow key={`${r.code} ${r.label}`} row={r}/>)}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default RiskProfileTab;
